import { useMutation } from '@tanstack/react-query'
import { MustMatchValidator } from '@thinknimble/tn-forms'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { isAxiosError } from 'axios'
import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import i18n from 'src/i18n/config'
import { AuthLayout } from 'src/components/auth-layout'
import { Button } from 'src/components/button'
import { ErrorMessage, ErrorsList } from 'src/components/errors'
import { PasswordInput } from 'src/components/password-input'
import { ResetPasswordForm, TResetPasswordForm, userApi } from 'src/services/user'

export const ResetPasswordInner = () => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler, overrideForm } = useTnForm<TResetPasswordForm>()
  const { userId, token } = useParams()
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const { mutate: confirmResetPassword } = useMutation({
    mutationFn: userApi.csc.resetPassword,
    onSuccess: () => {
      setSuccess(true)
    },
    onError: (e) => {
      if (isAxiosError(e) && e.response?.data && 'non-field-error' in e.response.data) {
        setError(e.response.data['non-field-error'])
        return
      }
      setError(t('auth.resetError'))
    },
  })

  useEffect(() => {
    if (token && userId) {
      overrideForm(ResetPasswordForm.create({ token: token, uid: userId }) as TResetPasswordForm)
    }
  }, [overrideForm, token, userId])

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (form.isValid) {
      confirmResetPassword({
        userId: form.value.uid!,
        token: form.value.token!,
        password: form.value.password!,
      })
    }
  }

  if (success) {
    return (
      <AuthLayout title={t('auth.resetSuccess')} description="">
        <Link to="/log-in" className="mt-3 text-sm font-semibold text-primary hover:underline">
          {t('auth.returnToLogin')}
        </Link>
      </AuthLayout>
    )
  }
  return (
    <AuthLayout title={t('auth.resetPasswordTitle')} description={t('auth.chooseNewPassword')}>
      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="flex w-full flex-col gap-3" onSubmit={onSubmit}>
          <section>
            <PasswordInput
              value={form.password.value}
              onChange={(e) => createFormFieldChangeHandler(form.password)(e.target.value)}
              className="w-full"
              placeholder={form.password.placeholder}
              tabIndex={1}
              iconTabIndex={4}
            />
            <ErrorsList errors={form.password.errors} />
          </section>
          <section>
            <PasswordInput
              value={form.confirmPassword.value}
              onChange={(e) => {
                createFormFieldChangeHandler(form.confirmPassword)(e.target.value)
              }}
              className="w-full"
              placeholder={form.confirmPassword.placeholder}
              tabIndex={2}
              iconTabIndex={5}
            />
            <ErrorsList errors={form.confirmPassword.errors} />
          </section>
          <Button variant="primary" type="submit" tabIndex={3}>
            {t('common.submit')}
          </Button>
        </form>
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
      </div>
    </AuthLayout>
  )
}

const formLevelValidators = {
  confirmPassword: new MustMatchValidator({
    message: i18n.t('auth.passwordsShouldMatch'),
    matcher: 'password',
  }),
}

export const ResetPassword = () => {
  return (
    <FormProvider formClass={ResetPasswordForm} formLevelValidators={formLevelValidators}>
      <ResetPasswordInner />
    </FormProvider>
  )
}
